/**
 * Gerenciador próprio — Google Consent Mode v2 (Basic).
 * ATENÇÃO: GTM pode conter outras tags. Auditar disparos e Consent Settings no GTM.
 */
export const CONSENT_COOKIE = 'feevale-consent-v2';
const MAX_AGE = 60 * 60 * 24 * 180;
const GA_ID = 'G-KT5REWJHJX';
const GOOGLE_ADS_ID = 'AW-1002781011';
const GTM_ID = 'GTM-KH655DN';
const CLARITY_ID = '42qce6gh4e';
const LINKEDIN_ID = '52865';

export function getConsent() {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie.split('; ').find(x => x.startsWith(CONSENT_COOKIE + '='));
  if (!raw) return null;
  try {
    const value = JSON.parse(decodeURIComponent(raw.slice(CONSENT_COOKIE.length + 1)));
    return value && value.version === 2 && typeof value.analytics === 'boolean' && typeof value.marketing === 'boolean'
      ? { analytics: value.analytics, marketing: value.marketing } : null;
  } catch (error) { return null; }
}

function writeConsent(value) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = CONSENT_COOKIE + '=' + encodeURIComponent(JSON.stringify({ version: 2, ...value })) +
    '; Path=/; Max-Age=' + MAX_AGE + '; SameSite=Lax' + secure;
}

function loadScript(src, id, onload) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  if (onload) script.onload = onload;
  document.head.appendChild(script);
}

function googleConsent(value) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag('consent', 'update', {
    analytics_storage: value.analytics ? 'granted' : 'denied',
    ad_storage: value.marketing ? 'granted' : 'denied',
    ad_user_data: value.marketing ? 'granted' : 'denied',
    ad_personalization: value.marketing ? 'granted' : 'denied',
    personalization_storage: value.marketing ? 'granted' : 'denied'
  });
}

function loadAnalytics() {
  if (window.__feevaleAnalyticsLoaded) return;
  window.__feevaleAnalyticsLoaded = true;
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: true });
  loadScript('https://www.googletagmanager.com/gtag/js?id=' + GA_ID, 'feevale-ga4');
}

function loadMarketing() {
  if (window.__feevaleMarketingLoaded) return;
  window.__feevaleMarketingLoaded = true;

  // Mantém a Google tag do Google Ads ativa apenas com consentimento de marketing.
  // Se GA4 já carregou gtag.js, reutiliza a mesma biblioteca.
  if (!window.__feevaleAnalyticsLoaded) {
    window.gtag('js', new Date());
    loadScript('https://www.googletagmanager.com/gtag/js?id=' + GOOGLE_ADS_ID, 'feevale-google-ads');
  }
  window.gtag('config', GOOGLE_ADS_ID);

  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  loadScript('https://www.googletagmanager.com/gtm.js?id=' + GTM_ID, 'feevale-gtm');

  // Demais rastreadores opcionais já presentes no projeto original.
  loadScript('https://www.clarity.ms/tag/' + CLARITY_ID, 'feevale-clarity');
  window._linkedin_partner_id = LINKEDIN_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (window._linkedin_data_partner_ids.indexOf(LINKEDIN_ID) === -1) {
    window._linkedin_data_partner_ids.push(LINKEDIN_ID);
  }
  loadScript('https://snap.licdn.com/li.lms-analytics/insight.min.js', 'feevale-linkedin');

  // Rubeus — código existente no antigo gatsby-ssr.js.
  loadScript('https://tracking.apprubeus.com.br/libs/RBTracking.min.js?rbclicod=2izxk9MrIfg5TsGst7zd', 'feevale-rubeus');

  // Navegg — preserva inicialização após o script terminar de carregar.
  window.naveggReady = window.naveggReady || [];
  loadScript('https://tag.navdmp.com/universal.min.js', 'feevale-navegg', function () {
    if (typeof window.Navegg === 'function') {
      window.nvg94655 = new window.Navegg({ acc: 94655 });
    }
  });

  // RD Station — carregamento apenas com permissão para marketing.
  loadScript('https://d335luupugsy2.cloudfront.net/js/loader-scripts/168bf134-2e44-4e66-ab9a-cdb7f59837c2-loader.js', 'feevale-rdstation');
}

export function applyConsent(value) {
  if (typeof window === 'undefined' || !value) return;
  googleConsent(value); // Sempre atualizar antes de injetar GTM/GA4.
  if (value.analytics) loadAnalytics();
  if (value.marketing) loadMarketing();
}

export function saveConsent(value) {
  if (typeof window === 'undefined') return;
  const previous = getConsent();
  writeConsent(value);
  applyConsent(value);
  // Tags já executadas não são desfeitas por um update. Recarrega ao revogar.
  if (previous && ((previous.analytics && !value.analytics) || (previous.marketing && !value.marketing))) {
    window.location.reload();
  }
  window.dispatchEvent(new Event('feevale-consent-change'));
}

export function initializeConsent() {
  if (typeof window === 'undefined') return;
  const value = getConsent();
  if (value) applyConsent(value);
}

export function trackPageView(location) {
  if (typeof window === 'undefined') return;
  const value = getConsent();
  if (!value) return;
  if (value.analytics && window.__feevaleAnalyticsLoaded) {
    window.gtag('event', 'page_view', {
      page_location: location.href,
      page_path: location.pathname + location.search,
      page_title: document.title
    });
  }
  if (value.marketing && window.__feevaleMarketingLoaded) {
    window.dataLayer.push({ event: 'gatsbyRouteChange', pagePath: location.pathname });
  }
}
